import React from 'react'
import { useHistory } from 'react-router-dom';
import SortableTable from '../partials/SortableTable'
import formatDate from '../../utils/formatDate'

/* ************************************************************************** */
export const JobList = ({
	status,
	jobs,
	isFetching,
	error,
	onSort,
	sortBy,
	sortDirection,
	filters,
	filterOptions,
	onFiltersChange,
	serverFiltering = false,
	options,
}) => {
  
	const history = useHistory();

	if (!isFetching && jobs.length === 0 && (!filters || !Object.keys(filters).length)) 
		return <span className="d-inline-block m-3 p-3 border rounded bg-light">No jobs found</span>
	
	let { list_count, column_choice, reference_label } = options

	// All possible columns that can be displayed
	let attachment = {name: '', value: 'attachments', sort: false, filter: false, custom:(attachments) => {
		if ( attachments.length > 0) {
			return <i className="far fa-file" title={attachments.length + ' file' + (attachments.length > 1 ? 's' : '')}></i>
		} else {
			return ('')
		}
	}};
	let link = {name: '', value: 'histories', sort: false, filter: false, custom:(links) => {
		if ( links.length > 0) {
			return <i className="fa fa-link" title={links.length + ' link' + (links.length > 1 ? 's' : '')}></i>
		} else {
			return ('')
		}
	}};
	let notes = {name: '', value: 'notes', sort: false, filter: false, custom:(notes) => {
		if ( notes.length > 0) {
			return <i className="fa fa-clipboard" title={notes.length + ' note' + (notes.length > 1 ? 's' : '')}></i>
		} else {
			return ('')
		}
	}}

	let name = {name: 'Name', value: 'name'};
	let status_header = {name: 'Status', value: 'status' };
	let reference_no = {name: reference_label, value: 'reference_no'};
	let ols_direct_no = {name: 'OLS Direct Number', value: 'directNo', custom:(directNo) => (
		<a href={window.ols_url + `onlineservices/construction/account_management/?direct_no=${directNo}`} target="_blank">
			{directNo}
		</a>
	)};
	let progress = {
		value: 'progress',
		name: 'Progress',
		filter: false,
		custom:(progress) => (
			<div className="progress-bar-container" style={{ verticalAlign: 'middle' }}>
				<div className="progress-bar" style={{ width: progress+'%' }} />
			</div>
		)
	};
	
	// user-configurable column
	let column = {}
	if ( column_choice == 'location' ) {
		column = {
			value: 'location_desc',
			name: 'Location',
			filterField: 'location_desc',
			filterValue: (job) => (
				job.location === null || job.location === undefined || job.location === ''
					? null
					: job.location
			),
			filterLabel: (job) => job.location_desc || '(Blank)',
			sortValue: (job) => job.location_desc || '',
		}
	} else {
		column = {
			value: 'form_variant',
			name: 'Form Logo',
			filterField: 'form_variant',
			filterValue: (job) => (
				job.form_variant_id === null || job.form_variant_id === undefined || job.form_variant_id === ''
					? null
					: job.form_variant_id
			),
			filterLabel: (job) => (job.form_variant != null ? job.form_variant.title : '(Blank)'),
			sortValue: (job) => (job.form_variant != null ? job.form_variant.title : ''),
			custom:(form_variant) => {
				return (form_variant != null ? form_variant.title : '')
			}
		}
	}

	let entry_user = {
		name: 'Created By',
		value: 'user',
		filterField: 'user',
		filterValue: (job) => (
			job.created_by === null || job.created_by === undefined || job.created_by === ''
				? (job.user?.id ?? null)
				: job.created_by
		),
		filterLabel: (job) => job.user?.name ?? 'Guest',
		sortValue: (job) => job.user?.name ?? 'Guest',
		custom:(user) => (
			user?.name ?? "Guest"
		)
	};
	let created_at = {
		name: 'Created On',
		value: 'created_at',
		date: true,
		filterLabel: (job) => (job.created_at ? formatDate(job.created_at) : '(Blank)'),
	};
	let review_at = {
		name: 'Marked for Review',
		value: 'review_at',
		date: true,
		filterLabel: (job) => (job.review_at ? formatDate(job.review_at) : '(Blank)'),
	};
	let archived_at = {
		name: 'Archived On',
		value: 'updated_at',
		date: true,
		filterLabel: (job) => (job.updated_at ? formatDate(job.updated_at) : '(Blank)'),
	};
	let imported_by = {
		name: 'Imported By',
		value: 'imported_by',
		filterField: 'imported_by',
		filterValue: (job) => {
			if (job.imported_by_id !== undefined && job.imported_by_id !== null && job.imported_by_id !== '') {
				return job.imported_by_id
			}
			// Search results keep the numeric user id on imported_by
			if (typeof job.imported_by === 'number') {
				return job.imported_by
			}
			if (job.imported_by === null || job.imported_by === undefined || job.imported_by === '') {
				return null
			}
			return job.imported_by
		},
		filterLabel: (job) => {
			if (typeof job.imported_by === 'string' && job.imported_by) {
				return job.imported_by
			}
			return '(Blank)'
		},
		sortValue: (job) => {
			if (typeof job.imported_by === 'string') {
				return job.imported_by
			}
			return ''
		},
	};
	let imported_at = {
		name: 'Date Imported',
		value: 'imported_at',
		date: true,
		filterLabel: (job) => (job.imported_at ? formatDate(job.imported_at) : '(Blank)'),
	};

	// Compile which columns to display, depending on the job list you're viewing
	let header_list = [];
	switch( status ) {
		case "open":
			header_list.push(attachment, link, notes, name, reference_no, progress, column, entry_user, created_at);
			break;
		case "pending":
			header_list.push(attachment, link, notes, name, reference_no, progress, column, entry_user, review_at);
			break;
		case "submitted":
			header_list.push(attachment, link, notes, name, ols_direct_no, reference_no, column, imported_by, imported_at);
			break;
		case "archived":
			header_list.push(attachment, link, notes, name, reference_no, progress, column, entry_user, archived_at);
			break;
		case "search":
			header_list.push(attachment, link, notes, name, status_header, reference_no, progress, column, entry_user, created_at);
			break;
	}

	const viewJob = (job) => {
		const { id } = job;
		history.push(`/job/${id}`)
	}
	
	const rowProps = (row) => ({
		onClick: viewJob.bind(this, row),
		style: { cursor: 'pointer' }
	})

	return (
		<SortableTable
			headers={[ ...header_list ]}
			info={jobs}
			all={true}
			rowProps={rowProps}
			onSort={onSort}
			sortBy={sortBy}
			sortDirection={sortDirection}
			filters={filters}
			filterOptions={filterOptions}
			onFiltersChange={onFiltersChange}
			serverFiltering={serverFiltering}
		/>
	)
}
